import React, { useState, useEffect } from 'react';
import { MapPin, Search, Building2, Users, Calendar } from 'lucide-react';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { Region } from '../../lib/sdk/index';
import { useRegions } from '../../hooks/useQueries';

const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

const Regions: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 350);
  const limit = 50;
  
  // Use React Query hooks with pagination
  const { data: regionsResponse, isLoading: loading } = useRegions({ 
    page, 
    limit, 
    search: debouncedSearchTerm || undefined 
  });
  
  const regions = Array.isArray(regionsResponse?.data) ? regionsResponse.data : [];
  const pagination = regionsResponse?.pagination;
  
  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);


  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Regions</h1>
        <p className="text-gray-600 dark:text-gray-400">View and search regions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <MapPin className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Regions</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{regions.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <Building2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Offices Allowed</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {regions.reduce((total, region) => total + (region.officeRange?.end - region.officeRange?.start + 1), 0)}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <Building2 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Current Offices</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {regions.reduce((total, region) => total + (region.currentOffices || 0), 0)}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <Users className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Available Offices</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {regions.reduce((total, region) => {
                  const allowed = region.officeRange?.end - region.officeRange?.start + 1;
                  const current = region.currentOffices || 0;
                  return total + (allowed - current);
                }, 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search regions by name or controller..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input pl-10 w-full"
          />
        </div>
      </div>

      <div className="card">
        {regions.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No regions found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by adding your first region.
            </p>
          </div>
        ) : (
          <>
            <TableRoot>
              <Table>
                <THead>
                  <TR>
                    <TH>Region ID</TH>
                    <TH>Name</TH>
                    <TH>Controller</TH>
                    <TH>Allowed Offices</TH>
                    <TH>Current Offices</TH>
                    <TH>Office Range</TH>
                  </TR>
                </THead>
                <TBody>
                  {regions.map((region) => (
                  <TR key={region._id}>
                    <TD className="font-medium">{region.regionId}</TD>
                    <TD>{region.name}</TD>
                    <TD>
                      {region.controllerId && typeof region.controllerId === 'object' && 'name' in region.controllerId 
                        ? (region.controllerId as any).name 
                        : 'Unassigned'
                      }
                    </TD>
                    <TD>{region.officeRange ? region.officeRange.end - region.officeRange.start + 1 : 0}</TD>
                    <TD>{region.currentOffices || 0}</TD>
                    <TD>
                      {region.officeRange ? 
                        `${region.officeRange.start}-${region.officeRange.end}` : 
                        'N/A'
                      }
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableRoot>
          
          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} regions
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={pagination.page === 1 || loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  Previous
                </button>
                <span className="flex items-center px-4 text-sm text-gray-700 dark:text-gray-300">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page === pagination.totalPages || loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default Regions;
